import { WKT } from "ol/format";
import { bbox as bboxStrategy } from "ol/loadingstrategy.js";
import { Vector as VectorSource } from "ol/source";
import { Style, Stroke, Fill, Circle } from "ol/style";
import { useMemo, useEffect, useState, useCallback } from "react";
import { AreaSection } from "../../types/Polygon";
import VectorLayerWrapper from "./VectorLayerWrapper";
import useTransientStore from "../../data/transcientStore";
import useStore from "../../data/store";
import { Feature } from "ol";
import { usePermissions } from "../../hooks/usePermissions";

import { BaseGeometry } from "../../types/BaseGeometry";
import { useTranslation } from "../../hooks/useTranslation";
import {
  getColorForStatus,
  getDefaultColorForGeometryType,
} from "../../utilities/StatusColors";

interface FeatureMapperProps {
  // Callback when user clicks empty map background (clears selection)
  onMapBackgroundClick?: () => void;
  // Callback for hover feature with geometry data
  onHoverFeature?: (
    geometryData: BaseGeometry | null,
    allOverlapping?: BaseGeometry[],
  ) => void;
  // Callback when a feature is clicked with full geometry data
  onFeatureSelectWithData?: (geometryData: BaseGeometry | null) => void;
  // Callback when multiple geometries overlap at click point
  onMultipleGeometriesClick?: (
    geometries: BaseGeometry[],
    clickPixel: [number, number],
  ) => void;
  visibleGeometries?: BaseGeometry[]; // new general visibles from treeData
  selectedGeometry?: BaseGeometry | null; // highlight
  autoCenterFeature?: (geom: BaseGeometry) => void; // callback for centering
  isDrawing?: boolean; // Disable hover when drawing is active
}

export default function FeatureMapper({
  onMapBackgroundClick,
  onHoverFeature,
  onFeatureSelectWithData,
  onMultipleGeometriesClick,
  visibleGeometries = [],
  selectedGeometry,
  autoCenterFeature,
  isDrawing = false,
}: Readonly<FeatureMapperProps>) {
  const selectedAddressSystem = useTransientStore(
    (state) => state.selectedAddressSystem,
  );
  const addrIdFromStore = selectedAddressSystem?.id || 0;

  const { getTranslation } = useTranslation({
    pageId: 340,
    addressSystemId: addrIdFromStore || 0,
    autoLoad: true,
  });
  const wktFormat = useMemo(() => new WKT(), []);
  const [hoverFeatures, setHoverFeatures] = useState<Feature[]>([]);
  // Removed subtle zoom tracking refs (no longer zooming on hover)

  // Check permission to show city objects on map
  const { checkAccess } = usePermissions();
  const canShowCityObjects = checkAccess("CITY_OBJECT_SHOW");

  const currentPolylines = useTransientStore((state) => state.currentPolylines);
  const currentPolygons = useTransientStore((state) => state.currentPolygons);
  const currentPolypoints = useTransientStore(
    (state) => state.currentPolypoints,
  );
  const searchFilter = useTransientStore((state) => state.searchFilter);
  const contractSearchActive = useTransientStore(
    (state) => state.contractSearchActive,
  );
  const filteredContractIds = useTransientStore(
    (state) => state.filteredContractIds,
  );
  const map = useTransientStore((state) => state.map);
  const setClickedPolygonIDs = useStore((state) => state.setClickedPolygonIDs);
  const selectedAddressSystemStatuses = useTransientStore(
    (state) => state.selectedAddressSystemStatuses,
  );
  const userGeometriesVisible = useTransientStore(
    (state) => state.userGeometriesVisible,
  );

  // (Selection handler moved below groupedFeatures declaration)

  // Hover handler (pointermove) - throttled (no zoom)
  useEffect(() => {
    if (!map) return;
    let frame: number | null = null;
    const handlePointerMove = (evt: any) => {
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;

        // Skip hover detection when drawing is active
        if (isDrawing) {
          onHoverFeature?.(null, []);
          setHoverFeatures([]);
          return;
        }

        const pixel = evt.pixel || map.getEventPixel(evt.originalEvent);

        // Collect ALL features at this pixel
        const allFeatures: Feature[] = [];
        map.forEachFeatureAtPixel(pixel, (feat: any) => {
          allFeatures.push(feat as Feature);
        });

        // Deduplicate by geometry ID and keep both feature and geometry data
        const uniqueMap = new Map<
          number | string,
          { feature: Feature; geometry: BaseGeometry }
        >();
        allFeatures.forEach((f) => {
          const geometryData = f.get("geometryData") as
            | BaseGeometry
            | undefined;
          if (geometryData) {
            const id = geometryData.featureId || geometryData.id;
            if (!uniqueMap.has(id)) {
              uniqueMap.set(id, { feature: f, geometry: geometryData });
            }
          }
        });

        const uniqueItems = Array.from(uniqueMap.values());
        const allGeometryData = uniqueItems.map((item) => item.geometry);
        const uniqueFeatures = uniqueItems.map((item) => item.feature);

        if (uniqueFeatures.length > 0) {
          const firstGeometry = allGeometryData[0];
          onHoverFeature?.(firstGeometry, allGeometryData);
          setHoverFeatures(uniqueFeatures);
        } else {
          onHoverFeature?.(null, []);
          setHoverFeatures([]);
        }
      });
    };
    const mapWithEvents = map as any;
    mapWithEvents.on("pointermove", handlePointerMove);
    return () => {
      mapWithEvents.un("pointermove", handlePointerMove);
      if (frame) cancelAnimationFrame(frame);
    };
  }, [map, onHoverFeature, isDrawing]);

  // Filter geometries based on search term and contract filters
  const filteredPolylines = useMemo(() => {
    let filtered = currentPolylines;

    // Apply text search filter
    if (searchFilter.trim()) {
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(searchFilter.toLowerCase()),
      );
    }

    // Apply contract filter if active
    if (contractSearchActive && filteredContractIds.length > 0) {
      filtered = filtered.filter((item) =>
        filteredContractIds.includes(item.contractId),
      );
    }

    return filtered;
  }, [
    currentPolylines,
    searchFilter,
    contractSearchActive,
    filteredContractIds,
  ]);

  const filteredPolygons = useMemo(() => {
    let filtered = currentPolygons;

    // Apply text search filter
    if (searchFilter.trim()) {
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(searchFilter.toLowerCase()),
      );
    }

    // Apply contract filter if active
    if (contractSearchActive && filteredContractIds.length > 0) {
      filtered = filtered.filter((item) =>
        filteredContractIds.includes(item.contractId),
      );
    }

    return filtered;
  }, [
    currentPolygons,
    searchFilter,
    contractSearchActive,
    filteredContractIds,
  ]);

  const filteredPolypoints = useMemo(() => {
    let filtered = currentPolypoints;

    // Apply text search filter
    if (searchFilter.trim()) {
      filtered = filtered.filter((item) =>
        item.name?.toLowerCase().includes(searchFilter.toLowerCase()),
      );
    }

    // Apply contract filter if active
    if (contractSearchActive && filteredContractIds.length > 0) {
      filtered = filtered.filter((item) =>
        filteredContractIds.includes(item.contractId),
      );
    }

    return filtered;
  }, [
    currentPolypoints,
    searchFilter,
    contractSearchActive,
    filteredContractIds,
  ]);

  // Combine previously filtered geometry sets with externally provided visible geometries (dedupe by featureId or id)
  // Only include geometries if user has permission to view city objects
  const baseCombined = canShowCityObjects
    ? [...filteredPolylines, ...filteredPolygons, ...filteredPolypoints]
    : [];
  const external =
    canShowCityObjects && Array.isArray(visibleGeometries)
      ? visibleGeometries.filter((g) => g.wkt)
      : [];
  const dedupMap = new Map<number, BaseGeometry>();
  for (const g of baseCombined) {
    if (g.featureId) dedupMap.set(g.featureId, g);
  }
  for (const g of external) {
    const fid = g.featureId || g.id;
    if (fid && !dedupMap.has(fid)) dedupMap.set(fid, g);
  }
  const geometries = Array.from(dedupMap.values());

  // Create a color mapping based on status - uses DB-stored colours
  const getColorForGeometry = useCallback(
    (stateId: number, geometryType: string): string => {
      // If no statuses loaded, use default colors
      if (selectedAddressSystemStatuses.length === 0) {
        return getDefaultColorForGeometryType(geometryType);
      }

      return getColorForStatus(
        stateId,
        selectedAddressSystemStatuses,
        geometryType,
      );
    },
    [selectedAddressSystemStatuses],
  );

  const groupedFeatures = useMemo(() => {
    const groups = new Map<string, { source: VectorSource; style: Style }>();

    const buildStyle = (type: string, stateId: number): Style => {
      const color = getColorForGeometry(stateId, type);

      switch (type) {
        case "Point":
          return new Style({
            image: new Circle({
              radius: 4,
              fill: new Fill({ color: color }),
              stroke: new Stroke({ color: "black", width: 1 }),
            }),
          });
        case "Polygon":
          return new Style({
            fill: new Fill({ color: color }),
            stroke: new Stroke({ color: "black", width: 1.5 }),
          });
        case "LineString":
        case "MultiLineString":
          return new Style({ stroke: new Stroke({ color: color, width: 2 }) });
        default:
          return new Style({ stroke: new Stroke({ color: color, width: 2 }) });
      }
    };

    for (const ms of geometries) {
      if (!ms.wkt) continue;
      let geometry;
      try {
        geometry = wktFormat.readGeometry(ms.wkt, {
          dataProjection: "EPSG:3067",
          featureProjection: "EPSG:3857",
        });
      } catch (error) {
        console.error(
          getTranslation("LABEL_FEATURE_MAPPER_WKT_PARSE_ERROR") ||
            "Error parsing WKT string:",
          ms.wkt,
          error,
        );
        continue;
      }
      if (!geometry) continue;

      const feature = new Feature({ geometry });
      feature.setProperties({
        geometryData: ms, // Store full BaseGeometry object
      });
      const rawType = geometry.getType();
      const groupKey = rawType === "MultiLineString" ? "LineString" : rawType;

      // Create a unique key combining geometry type and status for proper grouping
      const stateId = ms.stateId || 0;
      const uniqueKey = `${groupKey}-status-${stateId}`;

      if (!groups.has(uniqueKey)) {
        groups.set(uniqueKey, {
          source: new VectorSource({ strategy: bboxStrategy }),
          style: buildStyle(groupKey, stateId),
        });
      }
      groups.get(uniqueKey)?.source.addFeature(feature);
    }
    return groups;
  }, [geometries, wktFormat, getColorForGeometry]);

  // Add click handler after groupedFeatures is defined to avoid TDZ errors
  useEffect(() => {
    if (!map) return;
    const mapWithEvents = map as any;

    const handleMapClick = (event: any) => {
      const clickCoord = event.coordinate;
      const containing: Feature[] = [];

      // Collect all features that contain the click point
      groupedFeatures.forEach((group) => {
        group.source.getFeatures().forEach((feat) => {
          const geom = feat.getGeometry();
          if (!geom) return;
          const type = geom.getType();
          if (type === "Polygon" || type === "MultiPolygon") {
            if ((geom as any).intersectsCoordinate?.(clickCoord))
              containing.push(feat);
          }
        });
      });

      // Also check pixel-based features (for points and lines)
      const pixelFeatures: Feature[] = [];
      map.forEachFeatureAtPixel(event.pixel, (featureLike: any) => {
        pixelFeatures.push(featureLike as Feature);
        return undefined;
      });

      // Combine all features
      const allFeatures = [...containing, ...pixelFeatures];

      // Extract geometry data and deduplicate by ID
      const geometryDataMap = new Map<number | string, BaseGeometry>();
      allFeatures.forEach((f) => {
        const geometryData = f.get("geometryData") as BaseGeometry | undefined;
        if (geometryData) {
          const id = geometryData.featureId || geometryData.id;
          if (!geometryDataMap.has(id)) {
            geometryDataMap.set(id, geometryData);
          }
        }
      });

      const allGeometryData = Array.from(geometryDataMap.values());

      // If multiple geometries, show selection menu
      if (allGeometryData.length > 1) {
        onMultipleGeometriesClick?.(allGeometryData, [
          event.pixel[0],
          event.pixel[1],
        ]);
        return;
      }

      // If single geometry, select it directly
      if (allGeometryData.length === 1) {
        const geometryData = allGeometryData[0];
        setClickedPolygonIDs([
          String(geometryData.featureId || geometryData.id),
        ]);
        onFeatureSelectWithData?.(geometryData);
        return;
      }

      // No features clicked - clear selection
      setClickedPolygonIDs([]);
      onMapBackgroundClick?.();
    };

    mapWithEvents.on("singleclick", handleMapClick);
    return () => mapWithEvents.un("singleclick", handleMapClick);
  }, [
    map,
    groupedFeatures,
    setClickedPolygonIDs,
    onMapBackgroundClick,
    onFeatureSelectWithData,
    onMultipleGeometriesClick,
  ]);

  // Selected geometry highlight layer
  const highlightLayer = useMemo(() => {
    if (!selectedGeometry?.wkt) return null;
    try {
      const geom = wktFormat.readGeometry(selectedGeometry.wkt, {
        dataProjection: "EPSG:3067",
        featureProjection: "EPSG:3857",
      });
      if (!geom) return null;
      const feat = new Feature({ geometry: geom });
      const src = new VectorSource();
      src.addFeature(feat);
      const style = new Style({
        stroke: new Stroke({ color: "#ff6600", width: 3 }),
        fill: new Fill({ color: "rgba(255,102,0,0.25)" }),
      });
      return { source: src, style };
    } catch {
      return null;
    }
  }, [selectedGeometry, wktFormat]);

  // Hover highlight layers (distinct style) - highlight all overlapping features
  const hoverLayers = useMemo(() => {
    if (!hoverFeatures || hoverFeatures.length === 0) return [];

    const isMultiple = hoverFeatures.length > 1;

    return hoverFeatures
      .map((hoverFeature, index) => {
        const localId = hoverFeature.get("LocalID");
        // Avoid duplicating style if it's the selected one
        if (
          selectedGeometry &&
          localId &&
          (selectedGeometry.featureId === localId ||
            selectedGeometry.id === localId)
        ) {
          return null;
        }
        const geom = hoverFeature.getGeometry();
        if (!geom) return null;
        const src = new VectorSource();
        const clone = hoverFeature.clone();
        src.addFeature(clone);

        // Use different styling: yellow solid for single, purple dashed for multiple
        const style = new Style({
          stroke: new Stroke({
            color: isMultiple ? "#ff00ff" : "#ffd200",
            width: isMultiple ? 3 : 4,
            lineDash: isMultiple ? [8, 4] : undefined,
          }),
          fill: new Fill({
            color: isMultiple ? "rgba(255,0,255,0.12)" : "rgba(255,210,0,0.18)",
          }),
        });
        return { source: src, style, key: `hover-${localId}-${index}` };
      })
      .filter(
        (layer): layer is { source: VectorSource; style: Style; key: string } =>
          layer !== null,
      );
  }, [hoverFeatures, selectedGeometry]);

  // Auto-center when selectedGeometry changes
  useEffect(() => {
    if (selectedGeometry && autoCenterFeature) {
      autoCenterFeature(selectedGeometry);
    }
  }, [selectedGeometry, autoCenterFeature]);

  return (
    <>
      {Array.from(groupedFeatures.entries()).map(([key, group]) => (
        <VectorLayerWrapper
          key={key}
          source={group.source}
          style={group.style}
          zIndex={10}
          minZoom={2}
          maxZoom={25}
          visible={userGeometriesVisible}
        />
      ))}
      {highlightLayer && (
        <VectorLayerWrapper
          source={highlightLayer.source}
          style={highlightLayer.style}
          zIndex={50}
          minZoom={0}
          maxZoom={28}
          visible={userGeometriesVisible}
        />
      )}
      {/* Render all hover highlight layers */}
      {hoverLayers.map((layer, idx) => (
        <VectorLayerWrapper
          key={layer.key}
          source={layer.source}
          style={layer.style}
          zIndex={40 + idx}
          minZoom={0}
          maxZoom={28}
          visible={userGeometriesVisible}
        />
      ))}
    </>
  );
}
